import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LayoutWithSidebars from '../components/LayoutWithSidebars';
import './MyLibrary.css'; // Library-specific styles
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { database } from '../supabase';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import { BookCheck, BookText, Menu, PanelLeft, WalletCards } from 'lucide-react';

const MyLibrary = forwardRef(({ toggleSidebar }, ref) => {
    const [documents, setDocuments] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user, loading: authLoading, authInitialized } = useAuth();

    const toggleFlashcards = () => {
        // Scroll to flashcards section
        const flashcardsSection = document.getElementById('flashcards-section');
        if (flashcardsSection) {
            const offsetTop = flashcardsSection.offsetTop - 20; // 20px offset from top
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
        }
    };

    const toggleQuiz = () => {
        // Scroll to quizzes section
        const quizzesSection = document.getElementById('quizzes-section');
        if (quizzesSection) {
            const offsetTop = quizzesSection.offsetTop - 20; // 20px offset from top
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
        }
    };

    const toggleChat = () => {
        // For MyLibrary, we don't have a chat modal, so this is a no-op
        console.log('Chat not available in MyLibrary');
    };

    const handleDocumentsUpdate = (newDocuments) => {
        console.log('MyLibrary component received documents:', newDocuments);
        setDocuments(newDocuments);
    };




    // Fetch user data when user is available
    useEffect(() => {
        if (user) {
            fetchUserData();
        }
    }, [user]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            console.log('🔍 Fetching user data for:', user.uid);

            // Get Supabase user ID from Firebase UID
            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
            if (!supabaseUser) {
                console.log('❌ User not found in Supabase');
                return;
            }

            console.log('✅ Supabase user found:', supabaseUser.id);

            // Fetch all user data in parallel
            const [userDocuments, userQuizzes, userFlashcards] = await Promise.all([
                database.getUserDocuments(supabaseUser.id),
                SHOW_QUIZ_FEATURES ? database.getUserQuizzes(supabaseUser.id) : Promise.resolve([]),
                database.getUserFlashcards(supabaseUser.id)
            ]);

            console.log('📄 Documents fetched:', userDocuments);
            console.log('🧩 Quizzes fetched:', userQuizzes);
            console.log('🃏 Flashcards fetched:', userFlashcards);

            setDocuments(userDocuments || []);
            setQuizzes(userQuizzes || []);
            setFlashcards(userFlashcards || []);

        } catch (error) {
            console.error('❌ Error fetching user data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Show loading state while authentication is being checked
    if (authLoading) {
        return (
            <div className="main-layout">
                <div className="loading-container">
                    {/* <div className="loading-spinner"></div> */}
                    <p>Loading your library...</p>
                </div>
            </div>
        );
    }

    // Only redirect to landing if not authenticated AND auth is fully initialized
    // Check if we're in the middle of a redirect process by looking at URL params
    if (!user && authInitialized) {
        // Check if this is a redirect from Google (URL might contain auth params)
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthParams = urlParams.has('code') || urlParams.has('state') || 
                             window.location.hash.includes('access_token') ||
                             window.location.hash.includes('id_token');
        
        if (hasAuthParams) {
            // We're in the middle of a redirect, show loading instead of redirecting
            return (
                <div className="main-layout">
                    <div className="loading-container">
                        {/* <div className="loading-spinner"></div> */}
                        <p>Completing sign-in...</p>
                    </div>
                </div>
            );
        } else {
            // No auth params, safe to redirect to landing
            window.location.href = '/';
            return null;
        }
    }

    return (
        <div className="library-content">
                {/* Mobile Menu Header */}
                <div className="library-mobile-header">
                    <div className="header-left">
                        <h1 className="library-title">My Library</h1>
                        <div className="header-left-mobile">
                            <Menu onClick={toggleSidebar} className="header-left-mobile-icon" />
                        </div>
                    </div>
                </div>
                
                <div className="library-header">
                    <h1>My Library</h1>
                    <p>{SHOW_QUIZ_FEATURES ? 'Your saved quizzes, flashcards, and documents.' : 'Your saved flashcards and documents.'}</p>
                </div>
                    
                    {loading ? (
                        <div className="library-loading">
                            {/* <div className="loading-spinner"></div> */}
                            <p>Loading your library...</p>
                        </div>
                    ) : (
                        <div className="library-sections">
                            {SHOW_QUIZ_FEATURES && (
                            <div id="quizzes-section" className="library-section">
                                <h2><BookText className='library-section-icon' /> Quizzes ({quizzes.length})</h2>
                                {quizzes.length > 0 ? (
                                    <div className="library-grid">
                                        {quizzes.map((quiz) => (
                                            <div key={quiz.id} className="library-item quiz-item">
                                                <div className="item-header">
                                                    <h3>{quiz.document_name || 'Untitled Quiz'}</h3>
                                                    <span className="item-date">
                                                        {new Date(quiz.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <div className="item-content">
                                                    <p>{quiz.question_count || 0} questions</p>
                                                    <div className="item-actions quiz-item-actions-library">
                                                        <button onClick={() => window.location.href = `/take-quiz/${user.uid}/${quiz.id}`} className="action-btn primary my-library-btn"><BookCheck size={10} className='library-section-icon' /> Take Quiz</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <p>No quizzes created yet. Generate some quizzes to see them here!</p>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Flashcards Section */}
                            <div id="flashcards-section" className="library-section">
                                <h2><WalletCards className='library-section-icon' /> Flashcards ({flashcards.length})</h2>
                                {flashcards.length > 0 ? (
                                    <div className="library-grid">
                                        {flashcards.map((flashcard) => (
                                            <div key={flashcard.id} className="library-item flashcard-item">
                                                <div className="item-header">
                                                    <h3>{flashcard.document_name || 'Untitled Flashcards'}</h3>
                                                    <span className="item-date">
                                                        {new Date(flashcard.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <div className="item-content">
                                                    <p>{flashcard.flashcard_data?.length || 0} cards</p>
                                                    <div className="item-actions flashcard-item-actions-library">
                                                        <button onClick={() => window.location.href = `/study-flashcards/${user.uid}/${flashcard.id}`} className="action-btn primary my-library-btn"><WalletCards size={10} className='library-section-icon' /> Study</button>
                                                        {/* <button  className="action-btn secondary my-library-btn">Share</button> */}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <p>No flashcards created yet. Generate some flashcards to see them here!</p>
                                    </div>
                                )}
                            </div>

                            {/* Documents Section */}
                            {/* <div className="library-section">
                                <h2>📄 Documents ({documents.length})</h2>
                                {documents.length > 0 ? (
                                    <div className="library-grid">
                                        {documents.map((document) => (
                                            <div key={document.id} className="library-item document-item">
                                                <div className="item-header">
                                                    <h3>{document.document_name || 'Untitled Document'}</h3>
                                                    <span className="item-date">
                                                        {new Date(document.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <div className="item-content">
                                                    <p>{document.file_type || 'PDF'}</p>
                                                    <div className="item-actions">
                                                        <button className="action-btn primary">View</button>
                                                        <button className="action-btn secondary">Generate Quiz</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <p>No documents uploaded yet. Upload some PDFs to get started!</p>
                                    </div>
                                )}
                            </div> */}
                        </div>
                    )}
                </div>
    );
});

export default MyLibrary;